import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Collapse as RawCollapse } from 'react-collapse';
import ChevronUpIcon from '../../assets/icons/ChevronUpIcon';
import ChevronDownIcon from '../../assets/icons/ChevronDownIcon';
import { moneyFormat } from '../../utils/currencyTools';
import {
  RowContainer,
  Key,
  Value,
  DrawerCta,
  DetailRow,
  CloseDrawerWrapper,
  DrawerRow,
  DrawerTitle,
  DrawerCtaContainer,
  Price,
} from './LeviesAndFeesCollapse.styles';

interface PricedItems {
  formatted: string;
  label: string;
  price: number;
}

interface DetailsTableProps {
  fees: PricedItems[];
  totalFeesNumber: number | undefined;
}

const LeviesAndFeesCollapse: React.FC<DetailsTableProps> = ({ fees, totalFeesNumber }) => {
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery({ maxWidth: 767 });
  const lang = i18n.languages?.[0] || 'en';
  const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
  const [isAnimating, setIsAnimating] = useState(false);

  const handleDrawer = () => setDrawerOpen(!drawerOpen);
  const handleWork = () => setIsAnimating(true);
  const handleRest = () => setIsAnimating(false);

  if (!fees) return null;

  return (
    <>
      <DrawerRow>
        <DrawerTitle>{t(`pricingBreakdown.priceDetails.subHeading2`)}</DrawerTitle>
        <DrawerCtaContainer>
          <Price data-testid="levies-and-fees-sum-value">
            {typeof totalFeesNumber === 'number' && moneyFormat(totalFeesNumber, lang)}
          </Price>
          {isMobile && !drawerOpen && (
            <DrawerCta onClick={handleDrawer}>
              <p>{t(`pricingBreakdown.priceDetails.detailsButton`)}</p>
              <ChevronDownIcon rotate={!!drawerOpen} />
            </DrawerCta>
          )}
          {!isMobile && (
            <DrawerCta onClick={handleDrawer}>
              <ChevronDownIcon rotate={!!drawerOpen} />
            </DrawerCta>
          )}
        </DrawerCtaContainer>
      </DrawerRow>
      <RawCollapse isOpened={drawerOpen} onWork={handleWork} onRest={handleRest}>
        <>
          {(drawerOpen || isAnimating) && (
            <RowContainer data-testid="fee-list">
              {fees?.map((fee) => (
                <DetailRow key={fee.label} data-testid="fee">
                  <Key>
                    <Trans
                      i18nKey={fee.label}
                      // the global reset css imported in several feature apps
                      // contains globally applied oppinionated styling
                      components={{ small: <span style={{ fontSize: '12px' }} />, br: <br /> }}
                    />
                  </Key>
                  <Value data-testid="fee-value">
                    {lang === 'fr' ? `${fee.formatted.replace('$', '')} $` : fee.formatted}
                  </Value>
                </DetailRow>
              ))}
              {isMobile && drawerOpen && (
                <CloseDrawerWrapper>
                  <DrawerCta onClick={handleDrawer}>
                    <p>{t(`pricingBreakdown.priceDetails.closeDetailsButton`)}</p>
                    <ChevronUpIcon />
                  </DrawerCta>
                </CloseDrawerWrapper>
              )}
            </RowContainer>
          )}
        </>
      </RawCollapse>
    </>
  );
};

export default LeviesAndFeesCollapse;
